import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info';

export interface ToastLink {
    href: string;
    label: string;
    beforeNavigate?: () => void;
}

interface ToastProps {
    message: string;
    type?: ToastType;
    isVisible: boolean;
    onClose: () => void;
    duration?: number;
    link?: ToastLink;
}

const Toast: React.FC<ToastProps> = ({
    message,
    type = 'success',
    isVisible,
    onClose,
    duration = 5000,
    link,
}) => {
    const navigate = useNavigate();
    const [show, setShow] = useState(false);

    useEffect(() => {
        if (isVisible) {
            setShow(true);
            const timer = setTimeout(() => {
                setShow(false);
                setTimeout(onClose, 300); // Wait for fade out animation
            }, duration);
            return () => clearTimeout(timer);
        } else {
            setShow(false);
        }
    }, [isVisible, duration, onClose]);

    if (!isVisible && !show) return null;

    const bgColors = {
        success: 'bg-emerald-800',
        error: 'bg-red-600',
        info: 'bg-blue-600'
    };

    const icons = {
        success: <CheckCircle className="w-5 h-5 text-white" />,
        error: <AlertCircle className="w-5 h-5 text-white" />,
        info: <Info className="w-5 h-5 text-white" />
    };

    return (
        <div
            className={`
        flex items-center space-x-3
        px-6 py-4 rounded-xl shadow-2xl text-white
        transform transition-all duration-300 ease-in-out
        pointer-events-auto
        ${bgColors[type]}
        ${show ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
        >
            <div className="flex-shrink-0">
                {icons[type]}
            </div>
            <p className="font-medium text-sm pr-4">
                {message}
                {link && (
                    <>
                        {' '}
                        <a
                            href={link.href}
                            onClick={(event) => {
                                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
                                    return;
                                }
                                event.preventDefault();
                                link.beforeNavigate?.();
                                navigate(link.href);
                                onClose();
                            }}
                            className="underline underline-offset-2 font-semibold hover:text-white"
                        >
                            {link.label}
                        </a>
                    </>
                )}
            </p>
            <button
                onClick={() => setShow(false)}
                className="text-white/70 hover:text-white transition-colors"
            >
                <X className="w-4 h-4" />
            </button>
        </div>
    );
};

export default Toast;
